// Awareness-Hook: bindet Local-State (User + Cursor) an WebsocketProvider.awareness,
// observed Remote-States. Cursor-Updates auf 30Hz client-seitig gethrottlet (Plan §S4.6 + NFR-008).

import { useCallback, useEffect, useRef, useState } from "react";
import type { Awareness } from "y-protocols/awareness";
import type { WebsocketProvider } from "y-websocket";

export interface AwarenessUser {
  id: string;
  name: string;
  color: string;
}

export interface AwarenessCursor {
  x: number;
  y: number;
}

export interface AwarenessState {
  user: AwarenessUser;
  cursor: AwarenessCursor | null;
}

export interface RemotePresence {
  clientId: number;
  user: AwarenessUser;
  cursor: AwarenessCursor | null;
}

const CURSOR_THROTTLE_MS = 33; // ~30Hz

export function useAwareness(
  provider: WebsocketProvider | undefined,
  localUser: AwarenessUser | null,
): {
  remotePresences: RemotePresence[];
  setLocalCursor: (cursor: AwarenessCursor | null) => void;
} {
  const [remotePresences, setRemotePresences] = useState<RemotePresence[]>([]);
  const lastCursorAt = useRef(0);
  const pendingCursorRef = useRef<AwarenessCursor | null | undefined>(undefined);
  const rafRef = useRef<number | null>(null);

  // Initial-State (User + leerer Cursor) + Cleanup.
  useEffect(() => {
    if (!provider || !localUser) return;
    const awareness: Awareness = provider.awareness;
    awareness.setLocalStateField("user", localUser);
    awareness.setLocalStateField("cursor", null);
    return () => {
      awareness.setLocalState(null);
    };
  }, [provider, localUser]);

  // Remote-States observen.
  useEffect(() => {
    if (!provider) {
      setRemotePresences([]);
      return;
    }
    const awareness = provider.awareness;
    const refresh = (): void => {
      const myClientId = awareness.clientID;
      const next: RemotePresence[] = [];
      awareness.getStates().forEach((state, clientId) => {
        if (clientId === myClientId) return;
        if (!state || typeof state !== "object") return;
        const stateRecord = state as Record<string, unknown>;
        const user = parseUser(stateRecord.user);
        if (!user) return;
        const cursor = parseCursor(stateRecord.cursor);
        next.push({ clientId, user, cursor });
      });
      setRemotePresences(next);
    };
    refresh();
    awareness.on("change", refresh);
    return () => {
      awareness.off("change", refresh);
    };
  }, [provider]);

  const flushCursor = useCallback(() => {
    rafRef.current = null;
    if (!provider) return;
    if (pendingCursorRef.current === undefined) return;
    provider.awareness.setLocalStateField("cursor", pendingCursorRef.current);
    pendingCursorRef.current = undefined;
    lastCursorAt.current = Date.now();
  }, [provider]);

  const setLocalCursor = useCallback(
    (cursor: AwarenessCursor | null): void => {
      if (!provider) return;
      const now = Date.now();
      pendingCursorRef.current = cursor;
      if (now - lastCursorAt.current >= CURSOR_THROTTLE_MS) {
        flushCursor();
      } else if (rafRef.current === null) {
        rafRef.current = window.requestAnimationFrame(flushCursor);
      }
    },
    [provider, flushCursor],
  );

  // Beim Unmount pending Cursor abbrechen.
  useEffect(() => {
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return { remotePresences, setLocalCursor };
}

function parseUser(value: unknown): AwarenessUser | null {
  if (!value || typeof value !== "object") return null;
  const v = value as Record<string, unknown>;
  if (typeof v.id !== "string" || typeof v.name !== "string" || typeof v.color !== "string") {
    return null;
  }
  return { id: v.id, name: v.name, color: v.color };
}

function parseCursor(value: unknown): AwarenessCursor | null {
  if (!value || typeof value !== "object") return null;
  const v = value as Record<string, unknown>;
  if (typeof v.x !== "number" || typeof v.y !== "number") return null;
  return { x: v.x, y: v.y };
}

/**
 * Stabile Farbe pro User-ID via Hash → HSL. Reicht fuer V1 ohne User-Auswahl.
 */
export function colorForUserId(userId: string): string {
  let hash = 0;
  for (let i = 0; i < userId.length; i += 1) {
    hash = (hash * 31 + userId.charCodeAt(i)) | 0;
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 70%, 50%)`;
}
